import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Subscription.css";
import { useLocation } from "react-router-dom";
import paymentApi from "../../api/paymentApi";
import { useSelector } from "react-redux";
import { selectUserEmail, selectUserId } from "../Login/loginSlice";
import pormocodeApi from "../../api/promocodeApi";
import { IoMdArrowBack } from "react-icons/io";
import LegalEntityForm from "./LegalEntityForm";

const PLANS = {
    standard: {
        name: 'Стандарт',
        description: 'Для начала работы',
        badge: 'Популярный',
        prices: { 1: Number(process.env.REACT_APP_STANDART_MONTH1_PAY), 12: Number(process.env.REACT_APP_STANDART_MONTH12_PAY) },
        features: [
            { text: 'Юрист, бухгалтер, налоговый консультант', included: true },
            { text: 'Поддержка контекста в диалоге', included: true },
            { text: 'Загрузка и анализ файлов', included: true },
            { text: 'Судебно-претензионная работа', included: true },
            { text: 'Договорная работа', included: true },
            { text: 'Хранилище документов на 500мб и 10 файлов', included: true },
            { text: 'Ограничение по токенам: 1 млн. в месяц и 3 тыс. в день', included: true },
            { text: 'Голосовой ассистент', included: false },
        ],
        devices: 2,
    },
    pro: {
        name: 'ПРО',
        description: 'Максимум возможностей',
        prices: { 1: Number(process.env.REACT_APP_PRO_MONTH1_PAY), 12: Number(process.env.REACT_APP_PRO_MONTH12_PAY) },
        features: [
            { text: 'Всё из тарифа Стандарт', included: true },
            { text: 'Голосовой ассистент', included: true },
            { text: 'Определитель ОКОФ', included: true },
            { text: 'Хранилище документов на 1500мб и 1000 файлов', included: true },
            { text: 'Ограничение по токенам: 5 млн. в месяц и 1 млн. в день', included: true },
        ],
        devices: 5,
    }
};

export default function Subscription() {
    const location = useLocation();
    const navigate = useNavigate();
    const userEmail = useSelector(selectUserEmail);
    const userId = useSelector(selectUserId);
    const [loading, setLoading] = useState(false);
    const [agreeToAutoRenew, setAgreeToAutoRenew] = useState(true);
    const { paymentType } = location.state || { paymentType: 'subscription' };

    const [clientType, setClientType] = useState('individual'); // 'individual' | 'legal'
    const [selectedTier, setSelectedTier] = useState('pro');
    const [billingPeriod, setBillingPeriod] = useState(1);

    const [showPromo, setShowPromo] = useState(false);
    const [promoCode, setPromoCode] = useState("");
    const [discount, setDiscount] = useState(null);
    const [promoError, setPromoError] = useState(null);
    const [promoLoading, setPromoLoading] = useState(false);

    const [orderId] = useState(() => Math.floor(Math.random() * 1000000));

    const getPrice = (tier) => {
        const basePrice = PLANS[tier].prices[billingPeriod];
        return discount ? Math.round(basePrice * discount) : basePrice;
    };

    const getMonthlyPrice = (tier) => Math.round(getPrice(tier) / billingPeriod);

    const applyPromoCode = async () => {
        if (!promoCode.trim()) return;
        setPromoLoading(true);
        try {
            const response = await pormocodeApi.applyPromoCode(userEmail, promoCode, orderId);
            if (response) {
                setDiscount(0.8);
                setPromoError(null);
            }
        } catch (err) {
            setDiscount(null);
            setPromoError("Промокод недействителен");
        } finally {
            setPromoLoading(false);
        }
    };

    const handlePayment = async (tier) => {
        let email = userEmail;
        if (!email) {
            email = prompt("Введите ваш email:");
            if (!email) return;
            localStorage.setItem("userEmail", email);
        }

        setLoading(true);
        setSelectedTier(tier);

        try {
            const planKey = `${tier}_${billingPeriod === 1 ? '1_month' : '12_months'}`;
            const amount = paymentType === 'addcard' ? 1 : getPrice(tier);
            const autoPayEnabled = agreeToAutoRenew ? 1 : 0;

            const response = await paymentApi.initiatePayment(email, amount, orderId, paymentType, planKey, autoPayEnabled);

            if (response.data?.payment_url) {
                window.location.href = response.data.payment_url;
            } else if (response.data?.result === 'usebalance') {
                alert("Подписка оплачена баллами!");
            } else {
                alert("Ошибка при получении ссылки на оплату");
            }
        } catch (error) {
            console.error("Payment error:", error);
            alert("Ошибка при обработке платежа");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="subscription-container">
            <button className="back-button" onClick={() => navigate(-1)}>
                <IoMdArrowBack className="back-icon" />
            </button>
            <div className="subscription-header">
                <h1>{paymentType === 'addcard' ? 'Привязка карты' : 'Выберите тариф'}</h1>
                <p className="subtitle">Передовые ИИ-инструменты для юристов и бухгалтеров</p>
            </div>

            {/* Переключатель типа клиента */}
            <div className="client-type-toggle">
                <button
                    className={`client-type-btn ${clientType === 'individual' ? 'active' : ''}`}
                    onClick={() => setClientType('individual')}
                >
                    Физическое лицо
                </button>
                <button
                    className={`client-type-btn ${clientType === 'legal' ? 'active' : ''}`}
                    onClick={() => setClientType('legal')}
                >
                    Юридическое лицо
                </button>
            </div>

            {clientType === 'individual' ? (
                <>
                    {/* Переключатель периода */}
                    <div className="billing-toggle">
                        <button
                            className={`toggle-btn ${billingPeriod === 1 ? 'active' : ''}`}
                            onClick={() => setBillingPeriod(1)}
                        >
                            Ежемесячно
                        </button>
                        <button
                            className={`toggle-btn ${billingPeriod === 12 ? 'active' : ''}`}
                            onClick={() => setBillingPeriod(12)}
                        >
                            Ежегодно <span className="save-badge">−34%</span>
                        </button>
                    </div>

                    {/* Карточки тарифов */}
                    <div className="plans-container">
                        {Object.entries(PLANS).map(([key, plan]) => (
                            <div
                                key={key}
                                className={`plan-card ${selectedTier === key ? 'selected' : ''} ${key === 'pro' ? '' : 'featured'}`}
                                onClick={() => setSelectedTier(key)}
                            >
                                {plan.badge && <div className="plan-badge">{plan.badge}</div>}

                                <div className="plan-header">
                                    <h2 className="plan-name">{plan.name}</h2>
                                    <p className="plan-description">{plan.description}</p>
                                </div>

                                <div className="plan-pricing">
                                    <div className="price-row">
                                        <span className="price">{billingPeriod === 12 ? (getPrice(key)).toLocaleString('ru-RU') : getPrice(key).toLocaleString('ru-RU')}</span>
                                        <span className="currency">₽</span>
                                        <span className="period">/ {billingPeriod === 1 ? 'мес' : 'год'}</span>
                                    </div>
                                    {billingPeriod === 12 && (
                                        <div className="monthly-equivalent">
                                            {(getMonthlyPrice(key)).toLocaleString('ru-RU')} ₽/мес
                                        </div>
                                    )}
                                    {discount && <div className="discount-applied">Скидка 20% применена</div>}
                                </div>

                                <button
                                    className={`plan-btn ${key === 'pro' ? 'secondary' : 'primary'}`}
                                    onClick={(e) => { e.stopPropagation(); handlePayment(key); }}
                                    disabled={loading}
                                >
                                    {loading && selectedTier === key ? (
                                        <span className="loading-spinner"></span>
                                    ) : (
                                        `Выбрать ${plan.name}`
                                    )}
                                </button>

                                <div className="devices-info">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M4 6h16v10H4V6zm2 2v6h12V8H6zm-2 10h16v2H4v-2z" />
                                    </svg>
                                    До {plan.devices} устройств
                                </div>

                                <ul className="features-list">
                                    {plan.features.map((feature, idx) => (
                                        <li key={idx} className={feature.included ? '' : 'excluded'}>
                                            {feature.included ? (
                                                <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            ) : (
                                                <svg className="x-icon" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                            {feature.text}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {/* Согласие и промокод */}
                    <div className="subscription-footer-section">
                        <div className="checkbox-container">
                            <input
                                type="checkbox"
                                id="autoRenew"
                                className="custom-checkbox"
                                checked={agreeToAutoRenew}
                                onChange={() => setAgreeToAutoRenew(!agreeToAutoRenew)}
                            />
                            <label htmlFor="autoRenew" className="checkbox-label">
                                Автоматическое продление подписки
                            </label>
                        </div>

                        <div className="promo-section">
                            <button className="promo-toggle" onClick={() => setShowPromo(!showPromo)}>
                                {showPromo ? 'Скрыть промокод' : 'У меня есть промокод'}
                            </button>

                            {showPromo && (
                                <div className="promo-input-wrapper">
                                    <input
                                        type="text"
                                        placeholder="Введите промокод"
                                        value={promoCode}
                                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                        disabled={!!discount}
                                    />
                                    <button onClick={applyPromoCode} disabled={!!discount || promoLoading}>
                                        {promoLoading ? '...' : 'Применить'}
                                    </button>
                                    {discount && <span className="promo-success">Промокод применён!</span>}
                                    {promoError && <span className="promo-error">{promoError}</span>}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <LegalEntityForm
                    userId={userId}
                    onPaymentSuccess={() => navigate('/profile')}
                />
            )}

            <p className="subscription-footer">
                Корпоративные условия: <a href="mailto:sales@bottax.tech" className="contact-email">sales@bottax.tech</a>
            </p>
        </div>
    );
}